import React from "react";

const labelOf = (key) => key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return "";
  return String(value);
};

const formatMetric = (key, value) => {
  if (typeof value !== "number" || !/revenue|amount/i.test(key)) return displayValue(value);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat(undefined, {dateStyle: "medium", timeStyle: "short"}).format(date);
};

const ResourceAnalyticsPanel = ({analytics, loading, error}) => {
  if (loading) return <p className="text-sm text-stone-400">Loading analytics…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const entries = Object.entries(analytics && typeof analytics === "object" && !Array.isArray(analytics) ? analytics : {});
  const metrics = entries.filter(([, value]) => value === null || ["string", "number", "boolean"].includes(typeof value));
  const collections = entries.filter(([, value]) => Array.isArray(value));
  const nested = entries.filter(([, value]) => value && typeof value === "object" && !Array.isArray(value));

  if (!entries.length) return <p className="text-sm text-stone-400">No analytics available yet.</p>;

  return (
    <div className="space-y-5">
      {metrics.length ? (
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map(([key, value]) => (
            <div key={key} className="rounded-xl border border-blue-50 bg-blue-50/60 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-blue-500">{labelOf(key)}</dt>
              <dd className="mb-0 mt-1 break-words text-lg font-bold text-blue-800">{formatMetric(key, value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {collections.map(([key, items]) => {
        const isTrend = /trend/i.test(key);
        const amounts = isTrend
          ? items.map((item) => Number(item?.amount ?? item?.revenue ?? item?.downloads ?? item?.count ?? 0))
          : [];
        const maxAmount = Math.max(0, ...amounts);
        return (
          <section key={key} className="rounded-xl border border-blue-50 p-4">
            <h4 className="m-0 text-sm font-bold capitalize text-blue-800">{labelOf(key)}</h4>
            {!items.length ? (
              <p className="mb-0 mt-3 text-sm text-stone-400">No records yet.</p>
            ) : isTrend ? (
              <ul className="mb-0 mt-3 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((item, index) => {
                  const amount = amounts[index];
                  const label = item?.month ?? item?.date ?? item?.period ?? item?.label ?? `Period ${index + 1}`;
                  const metricKey = ["amount", "revenue", "downloads", "count"].find((field) => item?.[field] !== undefined) || "amount";
                  const width = maxAmount > 0 ? `${Math.max(3, (amount / maxAmount) * 100)}%` : "0%";
                  return (
                    <li key={`${label}-${index}`} className="min-w-0">
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                        <span className="truncate font-medium text-stone-600">{label}</span>
                        <span className="shrink-0 font-semibold text-blue-800">{formatMetric(metricKey, amount)}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-blue-50">
                        <div className="h-full rounded-full bg-blue-600 transition-all" style={{width}} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <ul className="mb-0 mt-3 grid list-none gap-2 p-0 sm:grid-cols-2">
                {items.map((item, index) => {
                  const fields = item && typeof item === "object"
                    ? Object.entries(item).filter(([, value]) => value === null || ["string", "number", "boolean"].includes(typeof value))
                    : [["", item]];
                  const title = item?.title || item?.name || item?.journalTitle || item?.bookTitle ||
                    item?.user?.name || item?.email || `Record ${index + 1}`;
                  return (
                    <li key={item?.id || index} className="rounded-lg bg-stone-50 px-3 py-2">
                      <p className="m-0 text-sm font-semibold text-stone-700">{title}</p>
                      <dl className="mb-0 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        {fields.filter(([field]) => !["title", "name", "journalTitle", "bookTitle"].includes(field)).map(([field, value]) => (
                          <div key={field || "value"} className="text-xs text-stone-500">
                            {field ? `${labelOf(field)}: ` : ""}{/date|at$/i.test(field) ? formatDate(value) : displayValue(value)}
                          </div>
                        ))}
                      </dl>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      {nested.map(([key, value]) => (
        <section key={key} className="rounded-xl border border-blue-50 p-4">
          <h4 className="m-0 text-sm font-bold capitalize text-blue-800">{labelOf(key)}</h4>
          <dl className="mb-0 mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(value).map(([field, item]) => (
              <div key={field}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-stone-400">{labelOf(field)}</dt>
                <dd className="mb-0 mt-1 break-words text-sm font-medium text-stone-700">
                  {displayValue(item) || (Array.isArray(item) ? `${item.length} records` : "Details available")}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
};

export default ResourceAnalyticsPanel;
